// src/utils/subDepStatusMutations.ts
// Pure functions for updating sub-dependency statuses in session state

import { Session, Platform, ComponentStatus } from '../types';

export interface SubDepStatus {
  subDepId: string;
  tested: boolean;
  testedPlatforms?: Platform[];
  bugged: boolean;
  buggedPlatforms?: Platform[];
  bugUrl?: string;
}

/**
 * Toggle a platform in the tested platforms list for a component.
 * Updates the tested status based on whether any platforms are selected.
 * Tracks testedAt timestamp per platform.
 */
export function toggleTestedPlatform(
  session: Session,
  componentId: string,
  platform: Platform
): Session {
  const componentStatuses = session.componentStatuses || [];
  const index = componentStatuses.findIndex(s => s.componentId === componentId);
  const now = new Date().toISOString();
  
  let updatedStatuses: ComponentStatus[];
  if (index === -1) {
    // Create new status entry with testedAt for this platform
    const newTestedAt: Partial<Record<Platform, string>> = { [platform]: now };
    updatedStatuses = [...componentStatuses, {
      componentId,
      tested: true,
      testedPlatforms: [platform],
      testedAt: newTestedAt,
      bugged: false
    }];
  } else {
    updatedStatuses = [...componentStatuses];
    const currentStatus = updatedStatuses[index];
    const testedPlatforms = currentStatus.testedPlatforms || [];
    const testedAt: Partial<Record<Platform, string>> = currentStatus.testedAt || {};
    
    // Toggle platform
    const isRemoving = testedPlatforms.includes(platform);
    const newTestedPlatforms = isRemoving
      ? testedPlatforms.filter(p => p !== platform)
      : [...testedPlatforms, platform];
    
    // Update testedAt: add timestamp when adding platform, remove when removing
    let newTestedAt: Partial<Record<Platform, string>> = { ...testedAt };
    if (isRemoving) {
      delete newTestedAt[platform];
    } else {
      // Only set timestamp if not already set (preserve original timestamp)
      if (!newTestedAt[platform]) {
        newTestedAt[platform] = now;
      }
    }
    
    // Update tested status: true if tested on any platform
    const newTested = newTestedPlatforms.length > 0;
    
    updatedStatuses[index] = {
      ...currentStatus,
      tested: newTested,
      testedPlatforms: newTestedPlatforms,
      testedAt: Object.keys(newTestedAt).length > 0 ? newTestedAt : undefined
    };
    
    // If unchecking all platforms and it's bugged, also uncheck bugged
    if (!newTested && currentStatus.bugged) {
      updatedStatuses[index] = { 
        ...updatedStatuses[index], 
        bugged: false, 
        buggedPlatforms: undefined
      };
    }
  }
  
  // Also update legacy subDepStatuses for backward compatibility
  let updatedLegacyStatuses = session.subDepStatuses || [];
  const subDepStatuses = session.subDepStatuses || [];
  const legacyIndex = subDepStatuses.findIndex(s => s.subDepId === componentId);
  updatedLegacyStatuses = [...subDepStatuses];
  
  if (legacyIndex === -1) {
    updatedLegacyStatuses = [...updatedLegacyStatuses, {
      subDepId: componentId,
      tested: updatedStatuses[updatedStatuses.length - 1].tested,
      testedPlatforms: updatedStatuses[updatedStatuses.length - 1].testedPlatforms,
      bugged: false
    }];
  } else {
    updatedLegacyStatuses[legacyIndex] = {
      ...updatedLegacyStatuses[legacyIndex],
      tested: updatedStatuses[index].tested,
      testedPlatforms: updatedStatuses[index].testedPlatforms,
      bugged: updatedStatuses[index].bugged ? updatedLegacyStatuses[legacyIndex].bugged : false
    };
  }
  
  return {
    ...session,
    componentStatuses: updatedStatuses,
    subDepStatuses: updatedLegacyStatuses
  };
}

/**
 * Toggle the bugged status for a sub-dependency.
 * When marking as bugged, also marks as tested and initializes buggedPlatforms.
 */
export function toggleBugged(
  session: Session,
  subDepId: string
): Session {
  const subDepStatuses = session.subDepStatuses || [];
  const index = subDepStatuses.findIndex(s => s.subDepId === subDepId);
  
  if (index === -1) {
    // Can't mark as bugged if not tested yet
    return session;
  }
  
  const updatedStatuses = [...subDepStatuses];
  const currentStatus = updatedStatuses[index];
  const newBuggedValue = !currentStatus.bugged;
  
  updatedStatuses[index] = { 
    ...currentStatus, 
    bugged: newBuggedValue,
    tested: newBuggedValue ? true : currentStatus.tested, // If marking as bugged, also mark as tested
    buggedPlatforms: newBuggedValue ? [] : undefined, // Initialize as empty array when bugged
    bugUrl: newBuggedValue ? (currentStatus.bugUrl || '') : undefined
  };
  
  return {
    ...session,
    subDepStatuses: updatedStatuses
  };
}

/**
 * Toggle a platform in the bugged platforms list for a sub-dependency.
 */
export function toggleBuggedPlatform(
  session: Session,
  subDepId: string,
  platform: Platform
): Session {
  const subDepStatuses = session.subDepStatuses || [];
  const index = subDepStatuses.findIndex(s => s.subDepId === subDepId);
  
  if (index === -1) return session; // Should not happen if bugged is checked
  
  const updatedStatuses = [...subDepStatuses];
  const currentStatus = updatedStatuses[index];
  const buggedPlatforms = currentStatus.buggedPlatforms || [];
  
  // Toggle platform
  const newBuggedPlatforms = buggedPlatforms.includes(platform)
    ? buggedPlatforms.filter(p => p !== platform)
    : [...buggedPlatforms, platform];
  
  updatedStatuses[index] = {
    ...currentStatus,
    buggedPlatforms: newBuggedPlatforms.length > 0 ? newBuggedPlatforms : undefined
  };
  
  return {
    ...session,
    subDepStatuses: updatedStatuses
  };
}

/**
 * Update the bug URL for a sub-dependency.
 */
export function updateBugUrl(
  session: Session,
  subDepId: string,
  bugUrl: string
): Session {
  const subDepStatuses = session.subDepStatuses || [];
  const index = subDepStatuses.findIndex(s => s.subDepId === subDepId);
  
  if (index === -1) return session;
  
  const updatedStatuses = [...subDepStatuses];
  updatedStatuses[index] = { ...updatedStatuses[index], bugUrl };
  
  return {
    ...session,
    subDepStatuses: updatedStatuses
  };
}

